import { useEffect, useCallback, useState, memo } from 'react';
import { useSelector, useDispatch, useTranslation } from '@ufc/core';
import { cn, Alert, AlertDescription, Skeleton, Badge, Checkbox, Label } from '@ufc/uikit';
import {
  weighbridgeOpsActions,
  selectZone,
  selectZoneFetching,
  selectZoneError,
  selectSocketStatus,
  selectConnectedAt,
  selectInternetConnected,
  selectFirstWeightPanel,
  selectSecondWeightPanel,
  selectFirstWeightQueue,
  selectSecondWeightQueue,
  selectManualOrderFirst,
  selectManualOrderSecond,
  selectManualDirectionFirst,
  selectManualDirectionSecond,
  selectFirstWeightHistory,
  selectSecondWeightHistory,
  selectFirstWeightIsLocked,
  selectFirstWeightLockedByOrder,
  selectSecondWeightIsLocked,
  selectSecondWeightLockedByOrder,
} from '../../store';
import { WeighScalePanel } from '../components/weigh-scale-panel';
import { GateControlCard } from '../components/gate-control-card';
import { ActiveQueuePanel } from '../components/active-queue-panel';
import { ManualOrderEntryCard } from '../components/manual-order-entry-card';
import { SessionHistoryPanel } from '../components/session-history-panel';
import { ConnectionStatusBadge } from '../components/connection-status-badge';
import type { WeighDirection, WeighStage, GateSide, GateConfig, WeighbridgeFlowIdentity, WeighbridgeQueueItem } from '../../domain/types';
import { buildFlowIdentity, getFlowIdentifier, getFlowDirection } from '../../domain/utils/flow-identity';

interface WeighbridgeOpsSectionProps {
  className?: string;
}

function getActiveQueueItem(
  queue: WeighbridgeQueueItem[],
  manualValue: string,
  manualDirection: WeighDirection,
): WeighbridgeQueueItem | null {
  if (!manualValue) {
    return queue[0] ?? null;
  }

  const manualIdentity = buildFlowIdentity(manualValue, manualDirection);
  const manualIdentifier = getFlowIdentifier(manualIdentity);
  return (
    queue.find((item) => {
      const itemIdentifier = getFlowIdentifier(item);
      return (
        itemIdentifier === manualIdentifier &&
        getFlowDirection(item) === manualDirection
      );
    }) ?? null
  );
}

function getActiveIdentity(
  queueItem: WeighbridgeQueueItem | null | undefined,
  manualValue: string,
  manualDirection: WeighDirection,
): WeighbridgeFlowIdentity {
  if (manualValue) {
    return buildFlowIdentity(manualValue, manualDirection);
  }

  if (queueItem) {
    return {
      direction: getFlowDirection(queueItem),
      orderNumber: queueItem.orderNumber,
      shipmentNumber: queueItem.shipmentNumber,
    };
  }

  return { direction: manualDirection };
}

function isOutboundFlow(
  queue: WeighbridgeQueueItem[],
  capturedItem: WeighbridgeQueueItem | null,
  manualOrder: string,
  manualDirection: WeighDirection,
): boolean {
  const item = getActiveQueueItem(queue, manualOrder, manualDirection) ?? capturedItem;
  return getFlowDirection(getActiveIdentity(item, manualOrder, manualDirection)) === 'outbound';
}

// ============================================================================
// Stage Panel — self-contained column for one weighing stage
// ============================================================================
interface StagePanelProps {
  stage: WeighStage;
  stageLabel: string;
  colorTheme: 'blue' | 'purple';
  queue: ReturnType<typeof selectFirstWeightQueue>;
  /** Snapshot of the order captured at this stage — kept after it leaves the
   *  queue so the panel still shows its context/live-net until Save. */
  capturedItem: WeighbridgeQueueItem | null;
  panel: ReturnType<typeof selectFirstWeightPanel>;
  history: ReturnType<typeof selectFirstWeightHistory>;
  manualOrder: string;
  manualDirection: WeighDirection;
  entryGate: GateConfig | undefined;
  exitGate: GateConfig | undefined;
  canControl: boolean;
  isLocked: boolean;
  lockedByOrder?: string;
  onCapture: () => void;
  onSave: () => void;
  saveDisabled?: boolean;
  onClearCapturedWeight: () => void;
  onManualChange: (v: string) => void;
  onManualDirectionChange: (direction: WeighDirection) => void;
  onRemoveFromQueue: (identity: WeighbridgeFlowIdentity, stage?: WeighStage) => void;
  onOpenGate: (stage: WeighStage, side: GateSide, gate: GateConfig) => void;
  onCloseGate: (stage: WeighStage, side: GateSide, gate: GateConfig) => void;
  onClearHistory: () => void;
  onOverweightContinue: () => void;
  onOverweightReject: () => void;
  isSaving: boolean;
}

const StagePanel = memo(function StagePanel({
  stage,
  stageLabel,
  colorTheme,
  queue,
  capturedItem,
  panel,
  history,
  manualOrder,
  manualDirection,
  entryGate,
  exitGate,
  canControl,
  isLocked,
  lockedByOrder,
  onCapture,
  onSave,
  saveDisabled,
  onClearCapturedWeight,
  onManualChange,
  onManualDirectionChange,
  onRemoveFromQueue,
  onOpenGate,
  onCloseGate,
  onClearHistory,
  onOverweightContinue,
  onOverweightReject,
  isSaving,
}: StagePanelProps) {
  const { t } = useTranslation();
  const activeQueueItem =
    getActiveQueueItem(queue, manualOrder, manualDirection) ?? capturedItem;
  const activeIdentity = getActiveIdentity(activeQueueItem, manualOrder, manualDirection);
  const activeIdentifier = getFlowIdentifier(activeIdentity);

  return (
    <div
      className={cn(
        'flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-sm lg:p-5 transition-colors',
        isLocked
          ? 'border-destructive/50 ring-1 ring-destructive/30 bg-destructive/5'
          : colorTheme === 'blue'
          ? 'border-blue-200/60 bg-gradient-to-b from-blue-50/50 to-transparent dark:border-blue-900/40 dark:from-blue-950/20'
          : 'border-purple-200/60 bg-gradient-to-b from-purple-50/50 to-transparent dark:border-purple-900/40 dark:from-purple-950/20'
      )}
    >
      {/* Stage header */}
      <div
        className={cn(
          'flex items-center justify-between rounded-xl px-4 py-3 shadow-sm ring-1 ring-inset',
          colorTheme === 'blue'
            ? 'bg-blue-100/50 ring-blue-200/50 text-blue-800 dark:bg-blue-900/20 dark:ring-blue-900/50 dark:text-blue-300'
            : 'bg-purple-100/50 ring-purple-200/50 text-purple-800 dark:bg-purple-900/20 dark:ring-purple-900/50 dark:text-purple-300'
        )}
      >
        <div className="flex items-center gap-3">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-background shadow-sm">
            <span className="text-base font-bold">
              {stage === 'first_weight' ? '1' : '2'}
            </span>
          </div>
          <h2 className="text-sm font-bold uppercase tracking-widest">
            {stageLabel}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {isLocked && (
            <Badge
              variant="destructive"
              className="flex items-center gap-1 px-2.5 py-1 text-xs font-semibold shadow-sm"
              title={
                lockedByOrder
                  ? t('weighbridgeOps.panel.stageLockedTitle', {
                      orderNumber: lockedByOrder,
                      defaultValue: `Locked by order ${lockedByOrder} — open exit gate to unlock`,
                    })
                  : t('weighbridgeOps.panel.stageLockedTitleNoOrder', {
                      defaultValue: 'Stage locked — open exit gate to unlock',
                    })
              }
            >
              <span>🔒</span>
              <span>
                {lockedByOrder
                  ? t('weighbridgeOps.panel.lockedBadge', {
                      orderNumber: lockedByOrder,
                    })
                  : t('weighbridgeOps.panel.lockedBadgeNoOrder')}
              </span>
            </Badge>
          )}
          {queue.length > 0 && (
            <Badge
              variant="secondary"
              className="px-2.5 py-0.5 text-xs font-semibold shadow-sm"
            >
              {t('weighbridgeOps.panel.inQueueBadge', { count: queue.length })}
            </Badge>
          )}
        </div>
      </div>

      {/* Lock warning banner */}
      {isLocked && (
        <div className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3.5 py-2.5 text-xs text-destructive dark:border-destructive/30 dark:bg-destructive/15">
          <span className="mt-0.5 shrink-0 text-sm">⚠️</span>
          <span>
            <span className="font-semibold">
              {t('weighbridgeOps.panel.stageLocked', {
                defaultValue: 'Stage locked',
              })}
            </span>
            {lockedByOrder
              ? ` ${t('weighbridgeOps.panel.stageLockedByOrder', {
                  orderNumber: lockedByOrder,
                  defaultValue: `— error for order ${lockedByOrder}.`,
                })}`
              : '.'}{' '}
            {t('weighbridgeOps.panel.stageLockedOpenGate', {
              defaultValue: 'Open the exit gate to unlock.',
            })}
          </span>
        </div>
      )}

      {/* Gates row */}
      <div className="grid grid-cols-2 gap-3">
        <GateControlCard
          stage={stage}
          side="entry"
          gate={entryGate}
          status={panel.entryGateStatus}
          canControl={canControl}
          onOpen={() => entryGate && onOpenGate(stage, 'entry', entryGate)}
          onClose={() => entryGate && onCloseGate(stage, 'entry', entryGate)}
        />
        <GateControlCard
          stage={stage}
          side="exit"
          gate={exitGate}
          status={panel.exitGateStatus}
          canControl={canControl}
          onOpen={() => exitGate && onOpenGate(stage, 'exit', exitGate)}
          onClose={() => exitGate && onCloseGate(stage, 'exit', exitGate)}
        />
      </div>

      {/* Queue */}
      <div className="rounded-xl border border-border/60 bg-card p-3.5 shadow-sm transition-all hover:shadow-md">
        <ActiveQueuePanel
          queue={queue}
          stage={stage}
          onRemove={onRemoveFromQueue}
        />
      </div>

      {/* Scale panel */}
      <div className="rounded-xl border border-border/60 bg-card p-4 shadow-sm transition-all hover:shadow-md lg:p-5">
        <WeighScalePanel
          panel={panel}
          orderNumber={activeIdentifier}
          activeQueueItem={activeQueueItem}
          direction={getFlowDirection(activeIdentity)}
          canCapture={canControl}
          stage={stage}
          onCapture={onCapture}
          onSave={onSave}
          saveDisabled={saveDisabled}
          onClearCapturedWeight={onClearCapturedWeight}
          onOverweightContinue={onOverweightContinue}
          onOverweightReject={onOverweightReject}
          isSaving={isSaving}
        />
      </div>

      {/* Manual order entry */}
      <div className="rounded-xl border border-border/60 bg-card p-3.5 shadow-sm transition-all hover:shadow-md">
        <ManualOrderEntryCard
          value={manualOrder}
          direction={manualDirection}
          onChange={onManualChange}
          onDirectionChange={onManualDirectionChange}
        />
      </div>

      {/* Per-stage session history */}
      <div className="rounded-xl border border-border/60 bg-card p-3.5 shadow-sm transition-all hover:shadow-md">
        <SessionHistoryPanel events={history} onClear={onClearHistory} />
      </div>
    </div>
  );
});

// ============================================================================
// Main Section
// ============================================================================
export function WeighbridgeOpsSection({
  className,
}: WeighbridgeOpsSectionProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const zone = useSelector(selectZone);
  const zoneFetching = useSelector(selectZoneFetching);
  const zoneError = useSelector(selectZoneError);
  const socketStatus = useSelector(selectSocketStatus);
  const connectedAt = useSelector(selectConnectedAt);
  const internetConnected = useSelector(selectInternetConnected);
  const firstWeight = useSelector(selectFirstWeightPanel);
  const secondWeight = useSelector(selectSecondWeightPanel);
  const fwQueue = useSelector(selectFirstWeightQueue);
  const swQueue = useSelector(selectSecondWeightQueue);
  const fwManualOrder = useSelector(selectManualOrderFirst);
  const swManualOrder = useSelector(selectManualOrderSecond);
  const fwManualDirection = useSelector(selectManualDirectionFirst);
  const swManualDirection = useSelector(selectManualDirectionSecond);
  const fwHistory = useSelector(selectFirstWeightHistory);
  const swHistory = useSelector(selectSecondWeightHistory);
  const fwIsLocked = useSelector(selectFirstWeightIsLocked);
  const fwLockedByOrder = useSelector(selectFirstWeightLockedByOrder);
  const swIsLocked = useSelector(selectSecondWeightIsLocked);
  const swLockedByOrder = useSelector(selectSecondWeightLockedByOrder);

  const canControl = true;

  // Snapshot of the order captured per stage. On Capture we remove the order
  // from the queue (UI + backend) immediately, but keep this snapshot so Save
  // and the weight display keep working until the operator finishes.
  const [capturedItems, setCapturedItems] = useState<
    Partial<Record<WeighStage, WeighbridgeQueueItem>>
  >({});
  const [qcPassed, setQcPassed] = useState(false);
  const outboundSecondWeight = isOutboundFlow(
    swQueue,
    capturedItems['second_weight'] ?? null,
    swManualOrder,
    swManualDirection,
  );

  useEffect(() => {
    dispatch(weighbridgeOpsActions.initPage());
    return () => {
      dispatch(weighbridgeOpsActions.disconnectSocket());
      dispatch(weighbridgeOpsActions.clearHistory());
    };
  }, [dispatch]);

  const handleCapture = useCallback(
    (stage: WeighStage) => {
      const queue = stage === 'first_weight' ? fwQueue : swQueue;
      const manualOrder =
        stage === 'first_weight' ? fwManualOrder : swManualOrder;
      const manualDirection =
        stage === 'first_weight' ? fwManualDirection : swManualDirection;
      const item =
        getActiveQueueItem(queue, manualOrder, manualDirection) ??
        capturedItems[stage] ??
        null;
      const identity = getActiveIdentity(item, manualOrder, manualDirection);
      if (!getFlowIdentifier(identity)) return;
      dispatch(
        weighbridgeOpsActions.captureWeightRequest({ ...identity, stage })
      );
      // Keep the order context alive for Save / weight display, then remove it
      // from the queue (UI + backend) so it doesn't reappear after navigation.
      if (item) setCapturedItems((prev) => ({ ...prev, [stage]: item }));
      dispatch(weighbridgeOpsActions.cancelQueueRequest({ ...identity, stage }));
    },
    [dispatch, fwQueue, swQueue, fwManualOrder, swManualOrder, fwManualDirection, swManualDirection, capturedItems]
  );

  const handleSave = useCallback(
    (stage: WeighStage) => {
      const queue = stage === 'first_weight' ? fwQueue : swQueue;
      const panel = stage === 'first_weight' ? firstWeight : secondWeight;
      const manualOrder =
        stage === 'first_weight' ? fwManualOrder : swManualOrder;
      const manualDirection =
        stage === 'first_weight' ? fwManualDirection : swManualDirection;
      // Item already left the queue on Capture — fall back to the snapshot.
      const item =
        getActiveQueueItem(queue, manualOrder, manualDirection) ??
        capturedItems[stage] ??
        null;
      const identity = getActiveIdentity(item, manualOrder, manualDirection);
      if (!getFlowIdentifier(identity) || panel.weight === undefined) return;
      dispatch(
        weighbridgeOpsActions.saveWeightRequest({
          ...identity,
          stage,
          weight: panel.weight,
          qcPassed: stage === 'second_weight' ? qcPassed : undefined,
        })
      );
      // Order is done at this stage — drop the snapshot.
      setCapturedItems((prev) => {
        const next = { ...prev };
        delete next[stage];
        return next;
      });
    },
    [
      dispatch,
      fwQueue,
      swQueue,
      fwManualOrder,
      swManualOrder,
      fwManualDirection,
      swManualDirection,
      firstWeight,
      secondWeight,
      capturedItems,
      qcPassed,
    ]
  );

  const handleClearCapturedWeight = useCallback(
    (stage: WeighStage) => {
      dispatch(weighbridgeOpsActions.clearPanelWeight({ stage }));
    },
    [dispatch]
  );

  const handleOpenGate = useCallback(
    (stage: WeighStage, side: GateSide, gate: GateConfig) => {
      dispatch(weighbridgeOpsActions.gateOpenRequest({ stage, side, gate }));
    },
    [dispatch]
  );

  const handleCloseGate = useCallback(
    (stage: WeighStage, side: GateSide, gate: GateConfig) => {
      dispatch(weighbridgeOpsActions.gateCloseRequest({ stage, side, gate }));
    },
    [dispatch]
  );

  const handleRemoveFromQueue = useCallback(
    (identity: WeighbridgeFlowIdentity, stage?: WeighStage) => {
      dispatch(
        weighbridgeOpsActions.cancelQueueRequest({ ...identity, stage })
      );
    },
    [dispatch]
  );

  const handleFwManualChange = useCallback(
    (v: string) => {
      dispatch(
        weighbridgeOpsActions.setManualOrderNumber({
          stage: 'first_weight',
          value: v,
        })
      );
    },
    [dispatch]
  );

  const handleSwManualChange = useCallback(
    (v: string) => {
      dispatch(
        weighbridgeOpsActions.setManualOrderNumber({
          stage: 'second_weight',
          value: v,
        })
      );
    },
    [dispatch]
  );

  const handleFwManualDirectionChange = useCallback(
    (direction: WeighDirection) => {
      dispatch(
        weighbridgeOpsActions.setManualDirection({
          stage: 'first_weight',
          direction,
        })
      );
    },
    [dispatch]
  );

  const handleSwManualDirectionChange = useCallback(
    (direction: WeighDirection) => {
      dispatch(
        weighbridgeOpsActions.setManualDirection({
          stage: 'second_weight',
          direction,
        })
      );
    },
    [dispatch]
  );

  const handleClearHistory = useCallback(
    () => dispatch(weighbridgeOpsActions.clearHistory()),
    [dispatch]
  );

  const handleFwCapture = useCallback(
    () => handleCapture('first_weight'),
    [handleCapture]
  );
  const handleSwCapture = useCallback(
    () => handleCapture('second_weight'),
    [handleCapture]
  );
  const handleFwSave = useCallback(
    () => handleSave('first_weight'),
    [handleSave]
  );
  const handleSwSave = useCallback(
    () => {
      handleSave('second_weight');
      setQcPassed(false);
    },
    [handleSave]
  );
  const handleQcPassedChange = useCallback((checked: boolean | 'indeterminate') => {
    setQcPassed(checked === true);
  }, []);
  const handleFwClearCapturedWeight = useCallback(
    () => handleClearCapturedWeight('first_weight'),
    [handleClearCapturedWeight]
  );
  const handleSwClearCapturedWeight = useCallback(
    () => handleClearCapturedWeight('second_weight'),
    [handleClearCapturedWeight]
  );

  const handleOverweightContinue = useCallback(
    (stage: WeighStage) => {
      const queue = stage === 'first_weight' ? fwQueue : swQueue;
      const manualOrder = stage === 'first_weight' ? fwManualOrder : swManualOrder;
      const manualDirection = stage === 'first_weight' ? fwManualDirection : swManualDirection;
      const identity = getActiveIdentity(queue[0], manualOrder, manualDirection);
      if (!getFlowIdentifier(identity)) return;
      dispatch(weighbridgeOpsActions.overweightContinueRequest({ stage, ...identity }));
    },
    [dispatch, fwQueue, swQueue, fwManualOrder, swManualOrder, fwManualDirection, swManualDirection]
  );

  const handleOverweightReject = useCallback(
    (stage: WeighStage) => {
      const queue = stage === 'first_weight' ? fwQueue : swQueue;
      const manualOrder = stage === 'first_weight' ? fwManualOrder : swManualOrder;
      const manualDirection = stage === 'first_weight' ? fwManualDirection : swManualDirection;
      const identity = getActiveIdentity(queue[0], manualOrder, manualDirection);
      if (!getFlowIdentifier(identity)) return;
      dispatch(weighbridgeOpsActions.overweightRejectRequest({ stage, ...identity }));
    },
    [dispatch, fwQueue, swQueue, fwManualOrder, swManualOrder, fwManualDirection, swManualDirection]
  );

  const handleFwOverweightContinue = useCallback(
    () => handleOverweightContinue('first_weight'),
    [handleOverweightContinue]
  );
  const handleFwOverweightReject = useCallback(
    () => handleOverweightReject('first_weight'),
    [handleOverweightReject]
  );
  const handleSwOverweightContinue = useCallback(
    () => handleOverweightContinue('second_weight'),
    [handleOverweightContinue]
  );
  const handleSwOverweightReject = useCallback(
    () => handleOverweightReject('second_weight'),
    [handleOverweightReject]
  );

  // ── Loading ──────────────────────────────────────────────────────────────
  if (zoneFetching) {
    return (
      <div className={cn('flex flex-col gap-4 p-6', className)}>
        <Skeleton className="h-10 w-80" />
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className="flex flex-col gap-3">
            <Skeleton className="h-8 w-full rounded-xl" />
            <div className="grid grid-cols-2 gap-2">
              <Skeleton className="h-24 rounded-xl" />
              <Skeleton className="h-24 rounded-xl" />
            </div>
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
          <div className="flex flex-col gap-3">
            <Skeleton className="h-8 w-full rounded-xl" />
            <div className="grid grid-cols-2 gap-2">
              <Skeleton className="h-24 rounded-xl" />
              <Skeleton className="h-24 rounded-xl" />
            </div>
            <Skeleton className="h-20 rounded-xl" />
            <Skeleton className="h-32 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (zoneError) {
    return (
      <div className={cn('p-6', className)}>
        <Alert variant="destructive">
          <AlertDescription>
            {t(zoneError, { defaultValue: zoneError })}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!zone) return null;

  if (!zone.controllerUrl) {
    return (
      <div className={cn('p-6', className)}>
        <Alert>
          <AlertDescription>
            {t('weighbridgeOps.zone.noController')}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const fwConfig = zone.gates?.first_weight;
  const swConfig = zone.gates?.second_weight;

  return (
    <div
      className={cn(
        'flex flex-col gap-6 p-4 sm:p-6 lg:p-8 bg-muted/10 min-h-full',
        className
      )}
    >
      {/* Page header */}
      <div className="flex flex-col gap-3 rounded-2xl bg-card p-5 shadow-sm ring-1 ring-border/50 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">
            {t('weighbridgeOps.title')}
          </h1>
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            {zone.factoryName && (
              <span className="font-medium text-foreground/80">
                {zone.factoryName}
              </span>
            )}
            {zone.factoryName && (
              <span className="h-1 w-1 rounded-full bg-muted-foreground/30"></span>
            )}
            <span className="rounded-md bg-muted px-1.5 py-0.5 font-mono text-xs font-medium">
              {zone.code}
            </span>
            <span className="text-muted-foreground/70">{zone.name}</span>
          </p>
        </div>
        <ConnectionStatusBadge
          status={socketStatus}
          connectedAt={connectedAt}
          internetConnected={internetConnected}
          className="shadow-sm"
        />
      </div>

      {/* Two symmetric stage columns */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
        {/* First Weight Column */}
        <StagePanel
          stage="first_weight"
          stageLabel={t('weighbridgeOps.stage.firstWeight')}
          colorTheme="blue"
          queue={fwQueue}
          capturedItem={capturedItems['first_weight'] ?? null}
          panel={firstWeight}
          history={fwHistory}
          manualOrder={fwManualOrder}
          manualDirection={fwManualDirection}
          entryGate={fwConfig?.entry}
          exitGate={fwConfig?.exit}
          canControl={canControl}
          isLocked={fwIsLocked}
          lockedByOrder={fwLockedByOrder}
          onCapture={handleFwCapture}
          onSave={handleFwSave}
          onClearCapturedWeight={handleFwClearCapturedWeight}
          onManualChange={handleFwManualChange}
          onManualDirectionChange={handleFwManualDirectionChange}
          onRemoveFromQueue={handleRemoveFromQueue}
          onOpenGate={handleOpenGate}
          onCloseGate={handleCloseGate}
          onClearHistory={handleClearHistory}
          onOverweightContinue={handleFwOverweightContinue}
          onOverweightReject={handleFwOverweightReject}
          isSaving={firstWeight.isSaving}
        />

        {/* Second Weight Column */}
        <div className="space-y-3">
          {outboundSecondWeight && <div className="flex min-h-11 items-center gap-3 rounded-md border bg-card p-3">
            <Checkbox
              id="outbound-qc-passed"
              checked={qcPassed}
              onCheckedChange={handleQcPassedChange}
            />
            <Label htmlFor="outbound-qc-passed" className="cursor-pointer">
              QC inspection passed for this outbound load
            </Label>
          </div>}
          <StagePanel
          stage="second_weight"
          stageLabel={t('weighbridgeOps.stage.secondWeight')}
          colorTheme="purple"
          queue={swQueue}
          capturedItem={capturedItems['second_weight'] ?? null}
          panel={secondWeight}
          history={swHistory}
          manualOrder={swManualOrder}
          manualDirection={swManualDirection}
          entryGate={swConfig?.entry}
          exitGate={swConfig?.exit}
          canControl={canControl}
          isLocked={swIsLocked}
          lockedByOrder={swLockedByOrder}
          onCapture={handleSwCapture}
          onSave={handleSwSave}
          saveDisabled={outboundSecondWeight && !qcPassed}
          onClearCapturedWeight={handleSwClearCapturedWeight}
          onManualChange={handleSwManualChange}
          onManualDirectionChange={handleSwManualDirectionChange}
          onRemoveFromQueue={handleRemoveFromQueue}
          onOpenGate={handleOpenGate}
          onCloseGate={handleCloseGate}
          onClearHistory={handleClearHistory}
          onOverweightContinue={handleSwOverweightContinue}
          onOverweightReject={handleSwOverweightReject}
          isSaving={secondWeight.isSaving}
          />
        </div>
      </div>
    </div>
  );
}
